import { Inject, Injectable } from '@angular/core';
import { concatMap, filter, map, Observable, of, take } from 'rxjs';
import {
  AbstractWebSocketService,
  WebSocketServiceConfig,
  WebSocketRequestOptions,
  ICommandResponse,
  USE_USB_PASSPORT_READER,
  USE_VERIFICATION_UPPER_MRZ,
  MRZ_RESOLUTION,
} from 'types';
import { CameraSettings } from '../dev/cam.auto.connect';
import { CameraServiceBase } from './camera.service';
import { HWServiceConfig } from './hw-service-config';
import { CameraInfo, CameraServiceState, CameraType, SizeInfo } from './hw-service-model';

export abstract class OpticScannerService {
  abstract init(): Observable<void>;

  abstract startGettingMrz(): Observable<void>;

  abstract getMrz(): Observable<string>;

  abstract stopGettingMrz(): Observable<void>;

  abstract done(): Observable<void>;
}

@Injectable()
export class BuiltinOpticScannerService extends AbstractWebSocketService implements OpticScannerService {
  constructor(config: HWServiceConfig) {
    super(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getOpticScannerProtocol()));
  }

  init(): Observable<void> {
    return this.ensureConnected();
  }

  done(): Observable<void> {
    return this.disconnect();
  }

  startGettingMrz(): Observable<void> {
    const request = { command: 'start-getting-mrz', progress: false };
    return this.sendRequest(request, { expectedJsons: 2, expectedBlobs: 0 }).pipe(map((res) => undefined));
  }

  stopGettingMrz(): Observable<void> {
    const request = { command: 'stop-getting-mrz', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((res) => undefined));
  }

  getMrz(): Observable<string> {
    const request = { command: 'get-mrz', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((res) => this.mapMrzResponse(res)));
  }

  private mapMrzResponse(response: ICommandResponse | Blob): string {
    if (response instanceof Blob) {
      throw 'Invalid response type (Blob)';
    }
    return atob(response['mrz']);
  }
}

export abstract class MrzReaderAdapter {
  abstract init(): Observable<CameraInfo[]>;

  abstract batterySupport(): boolean;

  abstract lights(enable: boolean): Observable<void>;

  abstract startReading(camera: CameraInfo): Observable<undefined | string>;

  abstract stopReading(): Observable<void>;

  abstract done(): Observable<void>;
}

@Injectable()
export class MrzReaderAdapterFactory {
  constructor(
    @Inject(USE_USB_PASSPORT_READER) public useUsbPassportReader: boolean,
    @Inject(USE_VERIFICATION_UPPER_MRZ) private useVerificationUpperMRZ: boolean,
    @Inject(MRZ_RESOLUTION) private mrzResolution: [number, number],
    private usbReader: OpticScannerService,
    private cameraReader: CameraServiceBase,
  ) {}

  create(): MrzReaderAdapter {
    return this.useUsbPassportReader
      ? new MrzReaderAdapterForIntech(this.usbReader)
      : new MrzReaderAdapterForLime(this.useVerificationUpperMRZ, this.mrzResolution, this.cameraReader);
  }
}

export class MrzReaderAdapterForIntech implements MrzReaderAdapter {
  constructor(private reader: OpticScannerService) {}

  init(): Observable<CameraInfo[]> {
    return this.reader.init().pipe(map((_) => [new CameraInfo(0, CameraType.Primary, [new SizeInfo(800, 600, null)])]));
  }

  // Для определения наличия батареи
  batterySupport(): boolean {
    return false;
  }

  lights(enable: boolean): Observable<void> {
    return of(undefined);
  }

  startReading(camera: CameraInfo): Observable<undefined | string> {
    let started = false;
    return this.reader.startGettingMrz().pipe(
      concatMap(() => {
        if (started) {
          return this.reader.getMrz();
        }
        started = true;
        return of(undefined);
      }),
    );
  }

  stopReading(): Observable<void> {
    return this.reader.stopGettingMrz();
  }

  done(): Observable<void> {
    return this.reader.done();
  }
}

export class MrzReaderAdapterForLime implements MrzReaderAdapter {
  constructor(private useVerificationUpperMRZ: boolean, private mrzResolution: [number, number], private reader: CameraServiceBase) {}

  init(): Observable<CameraInfo[]> {
    this.reader.connect();
    return this.reader.cameraState$.pipe(
      filter((state) => state === CameraServiceState.Ready),
      take(1),
      concatMap((state) => this.reader.getCameraList()),
    );
  }

  // Для определения наличия батареи
  batterySupport(): boolean {
    return true;
  }

  lights(enable: boolean): Observable<void> {
    this.reader.lights(enable);
    return of(undefined);
  }

  startReading(camera: CameraInfo): Observable<undefined | string> {
    const frame = CameraSettings.selectFrameByFormatThenArea(camera.frames, 'JPEG', this.mrzResolution[0] * this.mrzResolution[1]);
    this.reader.cameraId$ = camera.id;
    return this.reader.startMRZscan(frame, this.useVerificationUpperMRZ).pipe(map((res) => (res ? res.upper + res.lower : res)));
  }

  stopReading(): Observable<void> {
    return this.reader.stopPreview();
  }

  done(): Observable<void> {
    this.reader.disconnect();
    return this.reader.cameraState$.pipe(
      filter((state) => state === CameraServiceState.Closed),
      take(1),
      map((_) => undefined),
    );
  }
}
